import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Layers } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sistemaId: string;
  sistemaTipo: string;
  onCreated?: () => void;
}

type Modo = 'analogico' | 'convencional' | 'libre';

const normalizar = (txt: string | null | undefined) =>
  (txt ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const pad = (n: number, len: number) => String(n).padStart(len, '0');

export const CreacionMasivaElementosDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  sistemaId,
  sistemaTipo,
  onCreated,
}) => {
  const { toast } = useToast();
  const {
    tiposEquipo,
    marcasEquipo,
    modelosEquipo,
    recursosOcultos,
    cargarElementos,
  } = useSecurityContext();

  const sistemaTipoNorm = useMemo(() => normalizar(sistemaTipo), [sistemaTipo]);

  const [modo, setModo] = useState<Modo>('analogico');
  const [tipoEquipo, setTipoEquipo] = useState('');
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [fechaInstalacion, setFechaInstalacion] = useState('');
  const [anioFabricacion, setAnioFabricacion] = useState('');
  const [cantidad, setCantidad] = useState('10');

  // Analógico
  const [lazo, setLazo] = useState('1');
  const [direccionInicio, setDireccionInicio] = useState('1');
  const [zonaAnalogica, setZonaAnalogica] = useState('');

  // Convencional
  const [zona, setZona] = useState('1');
  const [numeroInicioConv, setNumeroInicioConv] = useState('1');

  // Libre
  const [prefijo, setPrefijo] = useState('');
  const [numeroInicioLibre, setNumeroInicioLibre] = useState('1');
  const [sufijo, setSufijo] = useState('');

  const [guardando, setGuardando] = useState(false);

  const tiposDelSistema = useMemo(
    () =>
      tiposEquipo.filter(
        (t) =>
          normalizar(t.sistema_tipo || '') === sistemaTipoNorm &&
          !recursosOcultos.tipos.has(t.id),
      ),
    [tiposEquipo, recursosOcultos.tipos, sistemaTipoNorm],
  );

  const marcasDelSistema = useMemo(
    () =>
      marcasEquipo.filter(
        (m) =>
          normalizar(m.sistema_tipo) === sistemaTipoNorm && !recursosOcultos.marcas.has(m.id),
      ),
    [marcasEquipo, recursosOcultos.marcas, sistemaTipoNorm],
  );
  const selectedMarca = marcasDelSistema.find((m) => m.nombre === marca);
  const modelosDelSistema = useMemo(
    () =>
      modelosEquipo.filter(
        (md) =>
          normalizar(md.sistema_tipo) === sistemaTipoNorm &&
          !recursosOcultos.modelos.has(md.id) &&
          (!selectedMarca || md.marca_id === selectedMarca.id),
      ),
    [modelosEquipo, recursosOcultos.modelos, sistemaTipoNorm, selectedMarca],
  );

  const cant = Math.min(Math.max(parseInt(cantidad || '0', 10) || 0, 0), 500);

  const filas = useMemo(() => {
    if (!tipoEquipo || cant <= 0) return [] as { nombre: string; zona: string | null; datos: Record<string, any> }[];
    const out: { nombre: string; zona: string | null; datos: Record<string, any> }[] = [];
    for (let i = 0; i < cant; i++) {
      if (modo === 'analogico') {
        const dir = (parseInt(direccionInicio || '1', 10) || 1) + i;
        const lz = parseInt(lazo || '1', 10) || 1;
        out.push({
          nombre: `${tipoEquipo} L${lz}-P${pad(dir, 2)}`,
          zona: zonaAnalogica || null,
          datos: {
            lazo: lz,
            direccion_elemento: dir,
            ...(zonaAnalogica ? { zona_asociada: zonaAnalogica } : {}),
          },
        });
      } else if (modo === 'convencional') {
        const n = (parseInt(numeroInicioConv || '1', 10) || 1) + i;
        out.push({
          nombre: `${tipoEquipo} ${pad(n, 2)} - Zona ${zona}`,
          zona: zona || null,
          datos: zona ? { zona_asociada: zona } : {},
        });
      } else {
        const n = (parseInt(numeroInicioLibre || '1', 10) || 1) + i;
        out.push({
          nombre: `${prefijo || tipoEquipo}${prefijo ? '' : ' '}${pad(n, 2)}${sufijo}`,
          zona: null,
          datos: {},
        });
      }
    }
    return out;
  }, [
    tipoEquipo,
    cant,
    modo,
    lazo,
    direccionInicio,
    zonaAnalogica,
    zona,
    numeroInicioConv,
    prefijo,
    numeroInicioLibre,
    sufijo,
  ]);

  const handleCrear = async () => {
    if (!tipoEquipo) {
      toast({ title: 'Falta el tipo de equipo', description: 'Selecciona el tipo de equipo a crear.', variant: 'destructive' });
      return;
    }
    if (filas.length === 0) {
      toast({ title: 'Cantidad no válida', description: 'Indica cuántos elementos quieres crear (1-500).', variant: 'destructive' });
      return;
    }
    if (anioFabricacion && !/^\d{4}$|^\d{2}\/\d{4}$/.test(anioFabricacion)) {
      toast({ title: 'Formato incorrecto', description: 'El año de fabricación debe ser AAAA o MM/AAAA.', variant: 'destructive' });
      return;
    }

    setGuardando(true);
    try {
      const rows = filas.map((f) => ({
        nombre: f.nombre,
        tipo: tipoEquipo,
        zona: f.zona,
        marca: marca || null,
        modelo: modelo || null,
        fecha_instalacion: fechaInstalacion || null,
        anio_fabricacion: anioFabricacion || null,
        observaciones: '',
        estado: 'activo',
        revisado: false,
        sistema_id: sistemaId,
        datos_tecnicos: f.datos,
      }));

      const { error } = await supabase.from('elementos').insert(rows);
      if (error) throw error;

      await cargarElementos();
      toast({
        title: 'Elementos creados',
        description: `Se han creado ${rows.length} elementos correctamente.`,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error en creación masiva:', error);
      toast({
        title: 'Error',
        description: error?.message || 'No se pudieron crear los elementos.',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const preview = filas.slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] mx-4 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" /> Creación masiva de elementos
          </DialogTitle>
          <DialogDescription>
            Crea varios elementos idénticos de golpe con numeración automática.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Datos base */}
          <div className="space-y-2">
            <Label>Tipo de equipo *</Label>
            <Select value={tipoEquipo} onValueChange={setTipoEquipo}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border border-border z-50">
                {tiposDelSistema.map((t) => (
                  <SelectItem key={t.id} value={t.nombre}>
                    {t.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marca</Label>
              <SearchableCombobox
                value={marca}
                placeholder="Selecciona marca..."
                options={marcasDelSistema.map((m) => ({ value: m.nombre, label: m.nombre }))}
                onChange={(v) => {
                  setMarca(v);
                  setModelo('');
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Modelo</Label>
              <SearchableCombobox
                value={modelo}
                placeholder="Selecciona modelo..."
                disabled={!selectedMarca}
                options={modelosDelSistema.map((m) => ({ value: m.nombre, label: m.nombre }))}
                onChange={setModelo}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Fecha de instalación</Label>
              <Input type="date" value={fechaInstalacion} onChange={(e) => setFechaInstalacion(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Año de fabricación</Label>
              <Input placeholder="AAAA o MM/AAAA" value={anioFabricacion} onChange={(e) => setAnioFabricacion(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Cantidad *</Label>
              <Input type="number" min={1} max={500} value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
            </div>
          </div>

          {/* Modo */}
          <Tabs value={modo} onValueChange={(v) => setModo(v as Modo)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="analogico">Analógico</TabsTrigger>
              <TabsTrigger value="convencional">Convencional</TabsTrigger>
              <TabsTrigger value="libre">Libre</TabsTrigger>
            </TabsList>

            <TabsContent value="analogico" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Lazo</Label>
                  <Input type="number" min={1} value={lazo} onChange={(e) => setLazo(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Dirección inicial</Label>
                  <Input type="number" min={1} value={direccionInicio} onChange={(e) => setDireccionInicio(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Zona asociada (opcional)</Label>
                  <Input value={zonaAnalogica} onChange={(e) => setZonaAnalogica(e.target.value)} placeholder="Ej: Planta 1" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="convencional" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Zona</Label>
                  <Input value={zona} onChange={(e) => setZona(e.target.value)} placeholder="Ej: 1 o Almacén" />
                </div>
                <div className="space-y-2">
                  <Label>Número inicial</Label>
                  <Input type="number" min={1} value={numeroInicioConv} onChange={(e) => setNumeroInicioConv(e.target.value)} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="libre" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Prefijo</Label>
                  <Input value={prefijo} onChange={(e) => setPrefijo(e.target.value)} placeholder="Ej: CAM-" />
                </div>
                <div className="space-y-2">
                  <Label>Número inicial</Label>
                  <Input type="number" min={1} value={numeroInicioLibre} onChange={(e) => setNumeroInicioLibre(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Sufijo</Label>
                  <Input value={sufijo} onChange={(e) => setSufijo(e.target.value)} placeholder="Ej: -Planta 1" />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Previsualización */}
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-sm font-medium mb-2">
              {filas.length > 0
                ? `Se generarán ${filas.length} elementos:`
                : 'Completa el tipo de equipo y la cantidad para ver la previsualización.'}
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              {preview.map((f, i) => (
                <li key={i}>• {f.nombre}</li>
              ))}
              {filas.length > 6 && <li>…</li>}
              {filas.length > 5 && <li>• {filas[filas.length - 1].nombre}</li>}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleCrear} disabled={guardando || filas.length === 0}>
            {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Crear {filas.length > 0 ? filas.length : ''} elementos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
